import React, { useState } from 'react';
import {
  TextInput,
  Text,
  View,
  StyleSheet,
  TextInputProps,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { colors } from '@/styles/colors';

interface PasswordInputProps extends Omit<TextInputProps, 'style'> {
  label: string;
  error?: string;
  containerStyle?: any;
}

// Eye icon component - simplified version without Defs/Mask/Use
const EyeIcon: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
  if (isVisible) {
    // Eye open icon
    return (
      <Svg width={22} height={14} viewBox="0 0 22 14" fill="none">
        <Path
          d="M11 9.72923C12.4441 9.72923 13.6267 8.51364 13.6267 7C13.6267 5.48636 12.4441 4.27077 11 4.27077C9.55585 4.27077 8.37333 5.48636 8.37333 7C8.37333 8.51364 9.55585 9.72923 11 9.72923Z"
          fill="#1976b5"
        />
        <Path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11 10.7692C8.97496 10.7692 7.33333 9.08169 7.33333 7C7.33333 4.91831 8.97496 3.23077 11 3.23077C13.025 3.23077 14.6667 4.91831 14.6667 7C14.6667 9.08169 13.025 10.7692 11 10.7692ZM11 1.04C7.49767 1.04 4.26772 2.9922 1.28359 7C4.26772 11.0078 7.49767 12.96 11 12.96C14.5023 12.96 17.7323 11.0078 20.7164 7C17.7323 2.9922 14.5023 1.04 11 1.04ZM11 14C6.94991 14 3.28325 11.6667 0 7C3.28325 2.33333 6.94991 0 11 0C15.0501 0 18.7168 2.33333 22 7C18.7168 11.6667 15.0501 14 11 14Z"
          fill="#1976b5"
        />
      </Svg>
    );
  }

  // Eye closed icon (with slash)
  return (
    <Svg width={22} height={18} viewBox="0 0 22 18" fill="none">
      <Path
        d="M11 9.72923C12.4441 9.72923 13.6267 8.51364 13.6267 7C13.6267 5.48636 12.4441 4.27077 11 4.27077C9.55585 4.27077 8.37333 5.48636 8.37333 7C8.37333 8.51364 9.55585 9.72923 11 9.72923Z"
        fill="#1976b5"
      />
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 10.7692C8.97496 10.7692 7.33333 9.08169 7.33333 7C7.33333 4.91831 8.97496 3.23077 11 3.23077C13.025 3.23077 14.6667 4.91831 14.6667 7C14.6667 9.08169 13.025 10.7692 11 10.7692ZM11 1.04C7.49767 1.04 4.26772 2.9922 1.28359 7C4.26772 11.0078 7.49767 12.96 11 12.96C14.5023 12.96 17.7323 11.0078 20.7164 7C17.7323 2.9922 14.5023 1.04 11 1.04ZM11 14C6.94991 14 3.28325 11.6667 0 7C3.28325 2.33333 6.94991 0 11 0C15.0501 0 18.7168 2.33333 22 7C18.7168 11.6667 15.0501 14 11 14Z"
        fill="#1976b5"
        opacity="0.5"
      />
      <Path
        d="M2 16L20 2"
        stroke="#1976b5"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Svg>
  );
};

export const PasswordInput: React.FC<PasswordInputProps> = ({
  label,
  error,
  containerStyle,
  ...inputProps
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.inputWrapper}>
        <TextInput
          style={[styles.input, error && styles.inputError]}
          secureTextEntry={!isVisible}
          placeholderTextColor="#999"
          autoCapitalize="none"
          autoCorrect={false}
          {...inputProps}
        />
        <TouchableOpacity
          style={styles.toggleButton}
          onPress={toggleVisibility}
          testID="toggle_visibility_button"
        >
          <EyeIcon isVisible={isVisible} />
        </TouchableOpacity>
      </View>
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: 14,
    paddingRight: 48,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    fontSize: 16,
    color: colors.textPrimary,
    backgroundColor: colors.inputBg,
  },
  inputError: {
    borderColor: colors.error,
  },
  toggleButton: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: [{ translateY: -7 }],
    padding: 4,
  },
  errorText: {
    color: colors.error,
    fontSize: 14,
    marginTop: 8,
  },
});
